/* eslint-disable no-restricted-syntax */
import {
  default as React,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type SyntheticEvent,
} from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import GroupsIcon from '@mui/icons-material/Groups';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import {
  AccessPickerOption,
  accessChipClass,
  useAccessPickerStyles,
} from './accessPickerUi';

interface CiamGroup {
  id: string;
  name: string;
  description: string;
}

/**
 * Scaffolder field for selecting WellSky Identity Client Groups.
 * Searches via POST /api/microapps-api/ciam/groups/search.
 * Application Groups (pub-* prefix) are already filtered server-side.
 *
 * Returns: string[] — array of group names (used in review step and provisioning).
 *
 * Usage in template YAML:
 *   ui:field: CiamGroupPicker
 */
export const CiamGroupPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<string[]>) => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const classes = useAccessPickerStyles();
  const chipClass = accessChipClass(classes, 'group');

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<CiamGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // formData is string[] of group names.
  const selected = useMemo<string[]>(
    () => (Array.isArray(formData) ? formData : []),
    [formData],
  );
  const selectedNames = useMemo(
    () => new Set(selected.map(n => n.toLowerCase())),
    [selected],
  );

  const backendUrl = config.getString('backend.baseUrl');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchGroups = useCallback(
    async (query: string) => {
      if (query.trim().length < 2) {
        setOptions([]);
        return;
      }
      setLoading(true);
      setFetchError(null);
      try {
        const { token } = await identityApi.getCredentials();
        const res = await fetch(
          `${backendUrl}/api/microapps-api/ciam/groups/search`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: 'include',
            body: JSON.stringify({ search: query.trim(), pageSize: 20 }),
          },
        );
        if (!res.ok) throw new Error(`Search failed: ${res.status}`);
        const data = (await res.json()) as { items: CiamGroup[] };
        setOptions(
          (data.items ?? []).filter(
            g => !selectedNames.has(g.name.toLowerCase()),
          ),
        );
      } catch (_err) {
        setFetchError('Failed to search client groups. Try again.');
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [backendUrl, identityApi, selectedNames],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void searchGroups(inputValue);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, searchGroups]);

  const handleSelect = useCallback(
    (_: SyntheticEvent, group: CiamGroup | null) => {
      if (!group) return;
      onChange([...selected, group.name]);
      setInputValue('');
      setOptions([]);
    },
    [selected, onChange],
  );

  const removeGroup = useCallback(
    (name: string) => {
      onChange(selected.filter(n => n !== name));
    },
    [selected, onChange],
  );

  const hasError = rawErrors !== undefined && rawErrors.length > 0;
  const noOptionsText =
    inputValue.trim().length < 2
      ? 'Type at least 2 characters to search'
      : fetchError ?? 'No client groups found';

  return (
    <FormControl fullWidth sx={{ mt: 2 }} error={hasError}>
      {selected.length > 0 && (
        <Box className={classes.chipRow}>
          {selected.map(name => (
            <Chip
              key={name}
              label={name}
              onDelete={() => removeGroup(name)}
              className={chipClass}
              size="small"
              icon={<GroupsIcon fontSize="small" />}
            />
          ))}
        </Box>
      )}

      <Autocomplete
        options={options}
        getOptionLabel={(opt: CiamGroup) => opt.name}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        inputValue={inputValue}
        onInputChange={(_, val, reason) => {
          if (reason !== 'reset') setInputValue(val);
        }}
        onChange={handleSelect}
        value={null}
        loading={loading}
        filterOptions={x => x}
        noOptionsText={noOptionsText}
        loadingText="Searching client groups…"
        className={classes.input}
        slotProps={{ paper: { className: classes.paper } }}
        renderInput={params => (
          <TextField
            {...params}
            label={schema?.title ?? 'Client Groups'}
            required={required && selected.length === 0}
            error={hasError}
            placeholder="Search by group name..."
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, opt) => {
          const { key: _key, ...liProps } =
            props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
          return (
            <AccessPickerOption
              key={opt.id}
              liProps={liProps}
              classes={classes}
              kind="group"
              primary={opt.name}
              secondary={opt.description || undefined}
            />
          );
        }}
      />

      {hasError && (
        <FormHelperText error>{rawErrors?.join(', ')}</FormHelperText>
      )}

      <FormHelperText sx={{ mt: 1, fontSize: '0.875rem' }}>
        {schema?.description ??
          'Select client groups whose members will have access to this app.'}
      </FormHelperText>
    </FormControl>
  );
};

export const CiamGroupPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'CiamGroupPicker',
    component: CiamGroupPicker,
  }),
);
